import * as React from "react";
import { Alert, Card } from "antd";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CardContent } from "../../components/antd/card/CardContent";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { CpSwitch } from "../gvc/testNewForm/CpSwitch";
import { CpTooltip } from "../gvc/testNewForm/CpTooltip";
import { CpLabelDirtyIndicator } from "../gvc/testNewForm/CpLabelDirtyIndicator";
import { useCpFormValue } from "../gvc/testNewForm/utils";
import { nameOfKind } from "../../services/cpln";
import { Policy } from "../../schema/types/policy/entity";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";

interface Props {
  policy: Policy;
}

export const PolicyTarget: React.FC<Props> = ({ policy }) => {
  const targetKind = useCpFormValue(`targetKind`);
  const targetAll: boolean = useCpFormValue(`targetAll`);
  const targetAllDisabled = ["org", "task"].includes(targetKind);
  const kindName = nameOfKind(targetKind);
  const org = useAppSelector(selectOrg);
  const isOrgKind = targetKind === "org";

  return (
    <div className="cpln-form-section cpln-ant-card-container">
      <Card>
        <CardTitle>Target</CardTitle>
        <CardContent>
          {isOrgKind ? (
            <Alert
              type="info"
              showIcon
              message={`Policy target is this org (${org}) itself.`}
              style={{ width: "fit-content" }}
            />
          ) : (
            <>
              <CpFormItem>
                <div className="flex items-center gap-2">
                  <CpLabel name={`targetAll`}>
                    Target All Items
                    <CpLabelDirtyIndicator name={`targetAll`} />
                  </CpLabel>
                  <CpSwitch name={`targetAll`} disabled={targetAllDisabled} />
                  <CpTooltip
                    tooltipProps={{
                      title: `When enabled, this policy applies to all ${kindName} resources in the organization. When disabled, use the Items and Query tabs to specify which resources are targeted.`,
                    }}
                  />
                </div>
                <CpErrorWarning name={`targetAll`} />
              </CpFormItem>

              {targetAll ? (
                <Alert
                  type="info"
                  showIcon
                  message={`This policy targets all ${kindName} resources in the organization.`}
                  description={
                    targetAllDisabled ? (
                      `Target All is always enabled for the "${kindName}" kind and cannot be changed.`
                    ) : (
                      <>
                        Every <strong>{kindName}</strong> resource in this organization is covered by this policy's bindings.
                        The <strong>Items</strong> and <strong>Query</strong> tabs are not needed when Target All is on.
                        To restrict the policy to specific resources, turn off Target All and use those tabs instead.
                      </>
                    )
                  }
                />
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message={`This policy targets specific ${kindName} resources.`}
                  description={
                    <>
                      Use the <strong>Items</strong> tab to select individual <strong>{kindName}</strong> resources, or the{" "}
                      <strong>Query</strong> tab to define filter conditions. A policy with no targets won't apply to any
                      resource. Turn on Target All to cover every {kindName} in the organization without needing a selection.
                    </>
                  }
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
